import { c as _c } from "react/compiler-runtime";
import { InfoCard, type InfoCardItem } from "@/components/InfoCard";
import { createTriggerItem, createJobsSummaryItem, createDurationItem, createTimeAgoItem } from "@/components/InfoCardUtils";
import { STATUS_COLORS } from "@/components/filters/StatusFilter";
import RunDetailsSkeleton from "@/components/RunDetailsSkeleton";
import { BackLink } from "@/components/ui/back-link";
import { StatusIcon } from "@/components/ui/status-icon";
import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@repo/blacksmith-ui/badge";
import { ScrollArea, ScrollBar } from "@repo/blacksmith-ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/blacksmith-ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@repo/blacksmith-ui/table";
import { Tabs, PillTabsList, PillTabsTrigger } from "@repo/blacksmith-ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { type WorkflowRunJob as Job, useRunDetailsByAttempt } from "@/hooks/useRunDetails";
import { useRunTests, type RunCheck } from "@/hooks/useRunTests";
import { TestList, type TestCheck } from "@/components/TestList";
import { TestListSkeleton } from "@/components/TestListSkeleton";
import { getJobGroupName, identifyJobType, type JobType } from "@/lib/runs";
import { usePrefetchJobDetails, useJobDetails } from "@/hooks/useJobDetails";
import { useStreamingJobLogs } from "@/hooks/useStreamingJobLogs";
import { cn } from "@repo/blacksmith-ui/cn";
import { formatSecondsToDuration } from "@/lib/utils";
import { isBlacksmithRunner } from "@/lib/environment";
import { SiGithub } from "@icons-pack/react-simple-icons";
import { ChevronDown, ChevronRight, FolderGit2, Redo, ListTree, FileCheck, SlashIcon, ExternalLink } from "lucide-react";
import { ErrorStateDisplay } from "./ErrorStateDisplay";
import { AxiosError } from "axios";
import Link from "next/link";
import { useDashboardAppRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useDashboardParams as useParams, useDashboardPathname as usePathname, useDashboardSearchParams as useSearchParams } from "@/hooks/useDashboardNavigation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { parseStatusFilters, type StatusFilterType, type BranchFilterType } from "@/hooks/useTestFilters";
import { useInfoStripCollidesWithTabs } from "@/hooks/useInfoStripCollidesWithTabs";
import { useKeyedState } from "@/hooks/useKeyedState";
import { useQueryParamWriter } from "@/hooks/useQueryParamWriter";
import { useLatestRef } from "@/hooks/useLatestRef";
import { useUrlParamSync } from "@/hooks/useUrlParamSync";
import { useValueChanged } from "@/hooks/useValueChanged";

// Type for a job group
interface JobGroup {
  name: string;
  jobs: Job[];
  minStartTime: Date | null;
  type: JobType;
}

// Helper function to get the current tab from sessionStorage for job pages
const getCurrentTab = (runId: string): string => {
  try {
    const storedTab = sessionStorage.getItem(`selectedJobTab_${runId}`);
    if (storedTab && ["logs", "metrics", "tests"].includes(storedTab)) {
      return storedTab;
    }
  } catch {
    // sessionStorage may not be available in some contexts
  }
  return "logs";
};

// Maximum number of failed jobs to prefetch details/metrics for on a run page.
const MAX_FAILED_JOBS_TO_PREFETCH = 5;

// Helper function to get the active run tab from URL params
const getActiveRunTab = (searchParams: URLSearchParams): string => {
  const tabFromUrl = searchParams.get("tab");
  const validTabs = ["overview", "tests"];
  return tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "overview";
};

// Component for the jobs list
const RunDetailsJobsList = t0 => {
  const $ = _c(55);
  const {
    org,
    runId,
    jobs,
    selectedAttempt,
    isActive: t1
  } = t0;
  const isActive = t1 === undefined ? true : t1;
  const prefetchJobDetails = usePrefetchJobDetails();
  const router = useRouter();
  const [selectedJobIndex, setSelectedJobIndex] = useKeyedState(jobs, 0);
  let t2;
  if ($[0] !== jobs) {
    const groups = {};
    for (const job of jobs) {
      const jobType = identifyJobType(job.name);
      const groupKey = jobType === "simple" ? `simple_${job.id}` : getJobGroupName(job.name);
      const displayName = getJobGroupName(job.name);
      if (!groups[groupKey]) {
        groups[groupKey] = {
          name: displayName,
          jobs: [],
          type: jobType,
          minStartTime: null
        };
      }
      groups[groupKey].jobs.push(job);
      const startTime = job.started_at ? new Date(job.started_at) : null;
      if (startTime && (!groups[groupKey].minStartTime || startTime < groups[groupKey].minStartTime)) {
        groups[groupKey].minStartTime = startTime;
      }
    }
    const getGroupSortPriority = _temp2;
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = (a, b) => {
        const priorityA = getGroupSortPriority(a);
        const priorityB = getGroupSortPriority(b);
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        if (!a.minStartTime) {
          return 1;
        }
        if (!b.minStartTime) {
          return -1;
        }
        return a.minStartTime.getTime() - b.minStartTime.getTime();
      };
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = Object.values(groups).sort(t3);
    $[0] = jobs;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const jobGroups = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [collapsedGroups, setCollapsedGroups] = useKeyedState(jobGroups, t3);
  let expanded;
  if ($[4] !== collapsedGroups || $[5] !== jobGroups) {
    expanded = {};
    for (const group_0 of jobGroups) {
      expanded[group_0.name] = !collapsedGroups[group_0.name];
    }
    $[4] = collapsedGroups;
    $[5] = jobGroups;
    $[6] = expanded;
  } else {
    expanded = $[6];
  }
  const expandedGroups = expanded;
  let visible;
  if ($[7] !== expandedGroups || $[8] !== jobGroups) {
    visible = [];
    for (const group_1 of jobGroups) {
      if (group_1.jobs.length === 1) {
        const job_2 = group_1.jobs[0];
        const labels_0 = job_2.labels || [];
        const runnerLabel_0 = labels_0[0] || "unknown";
        if (isBlacksmithRunner(runnerLabel_0)) {
          visible.push(job_2);
        }
      } else {
        if (expandedGroups[group_1.name]) {
          const sortedJobs = [...group_1.jobs].sort(_temp3);
          visible.push(...sortedJobs.filter(_temp4));
        }
      }
    }
    $[7] = expandedGroups;
    $[8] = jobGroups;
    $[9] = visible;
  } else {
    visible = $[9];
  }
  const visibleJobs = visible;
  let t4;
  let t5;
  if ($[10] !== selectedJobIndex || $[11] !== visibleJobs) {
    t4 = () => {
      const selectedJob = visibleJobs[selectedJobIndex];
      if (selectedJob) {
        const jobElement = document.querySelector(`[data-job-id="${selectedJob.id}"]`);
        if (jobElement && typeof jobElement.scrollIntoView === "function") {
          jobElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      }
    };
    t5 = [selectedJobIndex, visibleJobs];
    $[10] = selectedJobIndex;
    $[11] = visibleJobs;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== isActive || $[15] !== org || $[16] !== router || $[17] !== runId || $[18] !== selectedAttempt || $[19] !== selectedJobIndex || $[20] !== setSelectedJobIndex || $[21] !== visibleJobs) {
    t6 = () => {
      if (!isActive) {
        return;
      }
      const handleKeyDown = e => {
        if (!visibleJobs.length) {
          return;
        }
        const target = e.target as HTMLElement;
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
          return;
        }
        if ((e.key === "ArrowDown" || e.key === "ArrowUp") && (e.metaKey || e.ctrlKey)) {
          return;
        }
        bb235: switch (e.key) {
          case "j":
          case "ArrowDown":
            {
              e.preventDefault();
              setSelectedJobIndex(prev_0 => Math.min(prev_0 + 1, visibleJobs.length - 1));
              break bb235;
            }
          case "k":
          case "ArrowUp":
            {
              e.preventDefault();
              setSelectedJobIndex(_temp5);
              break bb235;
            }
          case "Enter":
            {
              e.preventDefault();
              const selectedJob_0 = visibleJobs[selectedJobIndex];
              if (selectedJob_0) {
                const currentTab = getCurrentTab(runId);
                const jobDetailsUrl = selectedAttempt ? `/${org}/runs/${runId}/jobs/${selectedJob_0.id}?attempt=${selectedAttempt}&tab=${currentTab}` : `/${org}/runs/${runId}/jobs/${selectedJob_0.id}?tab=${currentTab}`;
                router.push(jobDetailsUrl);
              }
            }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t7 = [isActive, visibleJobs, selectedJobIndex, org, runId, selectedAttempt, router, setSelectedJobIndex];
    $[14] = isActive;
    $[15] = org;
    $[16] = router;
    $[17] = runId;
    $[18] = selectedAttempt;
    $[19] = selectedJobIndex;
    $[20] = setSelectedJobIndex;
    $[21] = visibleJobs;
    $[22] = t6;
    $[23] = t7;
  } else {
    t6 = $[22];
    t7 = $[23];
  }
  useEffect(t6, t7);
  let t8;
  if ($[24] !== setCollapsedGroups) {
    t8 = groupName => {
      setCollapsedGroups(prev_1 => ({
        ...prev_1,
        [groupName]: !prev_1[groupName]
      }));
    };
    $[24] = setCollapsedGroups;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const toggleGroup = t8;
  let t9;
  bb0: {
    if (!jobs.length) {
      t9 = 0;
      break bb0;
    }
    let t10;
    if ($[26] !== jobs) {
      t10 = Math.max(...jobs.map(_temp6));
      $[26] = jobs;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    t9 = t10;
  }
  const maxDuration = t9;
  let t10;
  if ($[28] !== prefetchJobDetails || $[29] !== runId) {
    t10 = jobId => {
      prefetchJobDetails(runId, jobId);
    };
    $[28] = prefetchJobDetails;
    $[29] = runId;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const handleJobMouseEnter = t10;
  if (!jobs.length) {
    let t11;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="p-4 text-center text-sm text-neutral-500 dark:text-neutral-400">No jobs found</div>;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    return t11;
  }
  let t11;
  if ($[32] !== handleJobMouseEnter || $[33] !== maxDuration || $[34] !== org || $[35] !== runId || $[36] !== selectedAttempt || $[37] !== selectedJobIndex || $[38] !== visibleJobs) {
    t11 = (job_5, t12) => {
      const isNested = t12 === undefined ? false : t12;
      const labels_2 = job_5.labels || [];
      const runnerLabel_2 = labels_2[0] || "unknown";
      const isBlacksmithJob = isBlacksmithRunner(runnerLabel_2);
      const isRerun = selectedAttempt && selectedAttempt > 1 && job_5.workflow_run_attempt === selectedAttempt;
      const percentage = maxDuration > 0 ? Math.min(job_5.runtime_seconds / maxDuration * 100, 100) : 0;
      const statusColor = STATUS_COLORS[job_5.conclusion as keyof typeof STATUS_COLORS] || STATUS_COLORS.default;
      const currentTab_0 = getCurrentTab(runId);
      const jobDetailsUrl_0 = selectedAttempt ? `/${org}/runs/${runId}/jobs/${job_5.id}?attempt=${selectedAttempt}&tab=${currentTab_0}` : `/${org}/runs/${runId}/jobs/${job_5.id}?tab=${currentTab_0}`;
      const isSelected = visibleJobs[selectedJobIndex]?.id === job_5.id;
      return <TableRow key={job_5.id} data-job-id={job_5.id} className={`group text-sm transition-colors border-0 ${isBlacksmithJob ? "cursor-pointer hover:bg-neutral-50/50 dark:hover:bg-neutral-800/50" : "cursor-not-allowed opacity-50"} ${isSelected ? "bg-neutral-50/50 dark:bg-neutral-800/50" : ""}`} onMouseEnter={() => isBlacksmithJob && handleJobMouseEnter(job_5.id.toString())}><TableCell className={`py-1 ${isNested ? "pl-8" : ""}`}><div className="flex items-center gap-2"><StatusIcon status={job_5.conclusion || job_5.status || "unknown"} size={4} />{isBlacksmithJob ? <Link href={jobDetailsUrl_0} className="group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{isNested && identifyJobType(job_5.name) === "reusable" ? job_5.name.slice(job_5.name.indexOf(" / ") + 3) : job_5.name}</Link> : <span>{isNested && identifyJobType(job_5.name) === "reusable" ? job_5.name.slice(job_5.name.indexOf(" / ") + 3) : job_5.name}</span>}{isRerun && <Badge variant="secondary" className="ml-1 text-blue-500 border-blue-500 text-xs">Rerun</Badge>}{isBlacksmithJob && <><a href={job_5.job_url} target="_blank" rel="noopener noreferrer" className="opacity-0 group-hover:opacity-100 transition-opacity ml-2"><SiGithub className="w-4 h-4 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200" /></a><ChevronRight className="ml-auto w-4 h-4 opacity-0 group-hover:opacity-100 text-neutral-500 transition-opacity" /></>}</div></TableCell><TableCell className="px-4 py-1 text-xs text-neutral-500 dark:text-neutral-400 w-64 flex-shrink-0">{runnerLabel_2}</TableCell><TableCell className="py-1 w-32 flex-shrink-0"><div className="space-y-1 text-center"><div className="text-xs text-neutral-700 dark:text-neutral-300">{formatSecondsToDuration(job_5.runtime_seconds)}</div><div className="relative w-full h-1"><div className="absolute top-0 left-0 bottom-0 w-full h-full bg-neutral-100 rounded-full overflow-hidden dark:bg-neutral-800"><div className="absolute top-0 left-0 bottom-0 rounded-full" style={{
                  width: `${percentage}%`,
                  backgroundColor: statusColor
                }} /></div></div></div></TableCell></TableRow>;
    };
    $[32] = handleJobMouseEnter;
    $[33] = maxDuration;
    $[34] = org;
    $[35] = runId;
    $[36] = selectedAttempt;
    $[37] = selectedJobIndex;
    $[38] = visibleJobs;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const renderJobRow = t11;
  let t12;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TableHeader className="px-3 py-2 bg-[var(--color-surface)] select-none"><TableRow className="border-b text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"><TableHead className="h-8 w-[55%]">Job Name</TableHead><TableHead className="h-8 w-[30%]">Runner</TableHead><TableHead className="h-8 w-[15%]">Duration</TableHead></TableRow></TableHeader>;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== expandedGroups || $[42] !== jobGroups || $[43] !== renderJobRow || $[44] !== toggleGroup) {
    let t14;
    if ($[46] !== expandedGroups || $[47] !== renderJobRow || $[48] !== toggleGroup) {
      t14 = group_2 => {
        if (group_2.jobs.length === 1) {
          return renderJobRow(group_2.jobs[0], false);
        }
        return <React.Fragment key={group_2.name}><TableRow className="cursor-pointer hover:bg-neutral-50/50 dark:hover:bg-neutral-800/50 transition-colors border-0" onClick={() => toggleGroup(group_2.name)}><TableCell colSpan={3} className="py-1"><div className="flex items-center gap-2">{expandedGroups[group_2.name] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}{group_2.name}<Badge variant="secondary" className="ml-1 text-xs text-neutral-500 border-neutral-300 dark:text-neutral-400 dark:border-neutral-600">{`${group_2.type === "reusable" ? "Reusable workflow" : "Matrix"} (${group_2.jobs.length} jobs)`}</Badge></div></TableCell></TableRow>{expandedGroups[group_2.name] && group_2.jobs.sort(_temp7).map(job_6 => renderJobRow(job_6, true))}</React.Fragment>;
      };
      $[46] = expandedGroups;
      $[47] = renderJobRow;
      $[48] = toggleGroup;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    t13 = jobGroups.map(t14);
    $[41] = expandedGroups;
    $[42] = jobGroups;
    $[43] = renderJobRow;
    $[44] = toggleGroup;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[50] !== t13) {
    t14 = <Table>{t12}<TableBody>{t13}</TableBody></Table>;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ScrollBar orientation="horizontal" />;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t14) {
    t16 = <ScrollArea className="w-full border rounded-lg bg-[var(--color-surface)] mb-4">{t14}{t15}</ScrollArea>;
    $[53] = t14;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  return t16;
};

// Convert RunCheck to TestCheck for TestList compatibility
function replaceTestIdInUrl(pathname: string, testId: string | null) {
  const params = new URLSearchParams(window.location.search);
  if (testId) {
    params.set("test_id", testId);
  } else {
    params.delete("test_id");
  }
  window.history.replaceState(window.history.state, "", `${pathname}?${params.toString()}`);
}
function convertRunCheckToTestCheck(runCheck: RunCheck): TestCheck {
  return {
    id: runCheck.id,
    test_name: runCheck.test_name,
    test_suite: runCheck.test_suite,
    test_status: runCheck.test_status,
    test_type: runCheck.test_type,
    duration_seconds: runCheck.duration_seconds,
    logs: runCheck.logs,
    step_record_id: runCheck.step_record_id,
    step_order: runCheck.step_order,
    log_line_start: runCheck.log_line_start,
    log_line_end: runCheck.log_line_end,
    branch: runCheck.branch,
    job_id: runCheck.job_id,
    job_name: runCheck.job_name
  };
}

// Main component for workflow run summary
export function RunDetails(t0) {
  const $ = _c(241);
  const {
    runId,
    initialAttempt,
    updateBrowserTitle: t1,
    isSlideout: t2
  } = t0;
  const updateBrowserTitle = t1 === undefined ? false : t1;
  const isSlideout = t2 === undefined ? false : t2;
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    org
  } = useParams() as {
    org: string;
  };
  let t3;
  if ($[0] !== searchParams) {
    t3 = () => getActiveRunTab(searchParams);
    $[0] = searchParams;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [activeTab, setActiveTab] = useState(t3);
  let t4;
  if ($[2] !== searchParams) {
    t4 = getActiveRunTab(searchParams);
    $[2] = searchParams;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const urlTab = t4;
  if (useValueChanged(urlTab) && urlTab !== activeTab) {
    setActiveTab(urlTab);
  }
  const parseJobFilters = _temp8;
  let t5;
  if ($[4] !== searchParams) {
    t5 = searchParams.get("test_status") || searchParams.getAll("jobs").length > 0;
    $[4] = searchParams;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const urlHasFilters = !!t5;
  let t6;
  if ($[6] !== searchParams) {
    t6 = () => parseStatusFilters(searchParams.get("test_status"));
    $[6] = searchParams;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const [statusFilters, setStatusFiltersState] = useState(t6);
  const [timeRangeHours, setTimeRangeHours] = useState(24);
  const [branchFilter, setBranchFilter] = useState("all");
  const [zoomStart, setZoomStart] = useState(null);
  const [zoomEnd, setZoomEnd] = useState(null);
  let t7;
  if ($[8] !== urlHasFilters) {
    t7 = () => !urlHasFilters;
    $[8] = urlHasFilters;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const [isPendingDefaultFilter, setIsPendingDefaultFilter] = useState(t7);
  let t8;
  if ($[10] !== searchParams) {
    t8 = () => parseJobFilters(searchParams);
    $[10] = searchParams;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const [jobFilters, setJobFiltersState] = useState(t8);
  let t9;
  if ($[12] !== searchParams) {
    t9 = () => searchParams.get("test_id");
    $[12] = searchParams;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const [selectedTestId, setSelectedTestIdState] = useState(t9);
  const {
    updateQueryParams,
    isWriting: isWritingQueryParams
  } = useQueryParamWriter(router);
  let t10;
  if ($[14] !== searchParams) {
    t10 = searchParams.get("test_status");
    $[14] = searchParams;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const urlStatusParam = t10;
  let t11;
  if ($[16] !== statusFilters) {
    t11 = param => {
      const urlStatus = parseStatusFilters(param);
      const currentStatusStr = [...statusFilters].sort().join(",");
      const urlStatusStr = [...urlStatus].sort().join(",");
      if (urlStatusStr !== currentStatusStr) {
        setStatusFiltersState(urlStatus);
      }
    };
    $[16] = statusFilters;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== isWritingQueryParams || $[19] !== t11 || $[20] !== urlStatusParam) {
    t12 = {
      urlValue: urlStatusParam,
      isWriting: isWritingQueryParams,
      readSettled: _temp9,
      adopt: t11
    };
    $[18] = isWritingQueryParams;
    $[19] = t11;
    $[20] = urlStatusParam;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const markStatusWrite = useUrlParamSync(t12);
  let t13;
  if ($[22] !== searchParams) {
    t13 = JSON.stringify(parseJobFilters(searchParams));
    $[22] = searchParams;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => JSON.stringify(parseJobFilters(new URLSearchParams(window.location.search)));
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== jobFilters) {
    t15 = json => {
      const urlJobs = JSON.parse(json);
      const currentJobsStr = JSON.stringify([...jobFilters].sort());
      const urlJobsStr = JSON.stringify([...urlJobs].sort());
      if (urlJobsStr !== currentJobsStr) {
        setJobFiltersState(urlJobs);
      }
    };
    $[25] = jobFilters;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== isWritingQueryParams || $[28] !== t13 || $[29] !== t15) {
    t16 = {
      urlValue: t13,
      isWriting: isWritingQueryParams,
      readSettled: t14,
      adopt: t15
    };
    $[27] = isWritingQueryParams;
    $[28] = t13;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const markJobsWrite = useUrlParamSync(t16);
  let t17;
  if ($[31] !== markStatusWrite || $[32] !== updateQueryParams) {
    t17 = filters => {
      setStatusFiltersState(filters);
      const param_0 = filters.length > 0 ? filters.join(",") : null;
      markStatusWrite(param_0);
      updateQueryParams({
        test_status: param_0
      });
    };
    $[31] = markStatusWrite;
    $[32] = updateQueryParams;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const setStatusFilters = t17;
  let t18;
  if ($[34] !== markJobsWrite || $[35] !== updateQueryParams) {
    t18 = filters_0 => {
      setJobFiltersState(filters_0);
      markJobsWrite(JSON.stringify(filters_0));
      updateQueryParams({
        jobs: filters_0
      });
    };
    $[34] = markJobsWrite;
    $[35] = updateQueryParams;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const setJobFilters = t18;
  let t19;
  if ($[37] !== pathname) {
    t19 = testId => {
      setSelectedTestIdState(testId);
      if (typeof window === "undefined") {
        return;
      }
      replaceTestIdInUrl(pathname, testId);
    };
    $[37] = pathname;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const setSelectedTestId = t19;
  let t20;
  if ($[39] !== searchParams) {
    t20 = searchParams.get("test_id");
    $[39] = searchParams;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const urlTestId = t20;
  if (useValueChanged(urlTestId) && !isWritingQueryParams && urlTestId !== selectedTestId) {
    setSelectedTestIdState(urlTestId);
  }
  const selectedTestIdRef = useLatestRef(selectedTestId);
  let t21;
  let t22;
  if ($[41] !== isWritingQueryParams || $[42] !== selectedTestIdRef) {
    t21 = () => {
      if (isWritingQueryParams) {
        return;
      }
      const testId_0 = selectedTestIdRef.current;
      const params_0 = new URLSearchParams(window.location.search);
      if (params_0.get("test_id") !== testId_0) {
        replaceTestIdInUrl(window.location.pathname, testId_0);
      }
    };
    t22 = [isWritingQueryParams, selectedTestIdRef];
    $[41] = isWritingQueryParams;
    $[42] = selectedTestIdRef;
    $[43] = t21;
    $[44] = t22;
  } else {
    t21 = $[43];
    t22 = $[44];
  }
  useEffect(t21, t22);
  const {
    data,
    currentAttempt,
    currentAttemptJobs,
    isLoading,
    error,
    selectedAttempt,
    setSelectedAttempt,
    availableAttempts
  } = useRunDetailsByAttempt(runId, initialAttempt);
  const {
    tests: runTests,
    isLoading: isLoadingTests,
    error: testsError
  } = useRunTests(runId, true);
  let t23;
  if ($[45] !== runTests) {
    t23 = runTests.map(convertRunCheckToTestCheck);
    $[45] = runTests;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const testsAsTestChecks = t23;
  const [defaultFailedFilterApplied, setDefaultFailedFilterApplied] = useState(false);
  const defaultFailedFilterWrittenRef = useRef(false);
  if (isPendingDefaultFilter && !isLoadingTests) {
    const hasFailures = !urlHasFilters && runTests.some(_temp10);
    if (hasFailures) {
      setStatusFiltersState(["failed"]);
      setDefaultFailedFilterApplied(true);
    }
    setIsPendingDefaultFilter(false);
  }
  let t24;
  let t25;
  if ($[47] !== defaultFailedFilterApplied || $[48] !== markStatusWrite || $[49] !== updateQueryParams) {
    t24 = () => {
      if (!defaultFailedFilterApplied || defaultFailedFilterWrittenRef.current) {
        return;
      }
      defaultFailedFilterWrittenRef.current = true;
      markStatusWrite("failed");
      updateQueryParams({
        test_status: "failed"
      });
    };
    t25 = [defaultFailedFilterApplied, markStatusWrite, updateQueryParams];
    $[47] = defaultFailedFilterApplied;
    $[48] = markStatusWrite;
    $[49] = updateQueryParams;
    $[50] = t24;
    $[51] = t25;
  } else {
    t24 = $[50];
    t25 = $[51];
  }
  useEffect(t24, t25);
  const hasTests = runTests && runTests.length > 0;
  let t26;
  bb0: {
    if (!runTests) {
      t26 = 0;
      break bb0;
    }
    let t27;
    if ($[52] !== runTests) {
      t27 = runTests.filter(_temp11);
      $[52] = runTests;
      $[53] = t27;
    } else {
      t27 = $[53];
    }
    t26 = t27.length;
  }
  const failingTestsCount = t26;
  let t27;
  bb1: {
    if (!selectedTestId || !runTests.length) {
      t27 = null;
      break bb1;
    }
    let t28;
    if ($[54] !== runTests || $[55] !== selectedTestId) {
      let t29;
      if ($[57] !== selectedTestId) {
        t29 = t_1 => t_1.id === selectedTestId;
        $[57] = selectedTestId;
        $[58] = t29;
      } else {
        t29 = $[58];
      }
      t28 = runTests.find(t29);
      $[54] = runTests;
      $[55] = selectedTestId;
      $[56] = t28;
    } else {
      t28 = $[56];
    }
    const selectedTest = t28;
    t27 = selectedTest?.job_id ?? null;
  }
  const selectedTestJobId = t27;
  const {
    data: selectedJobDetails,
    loadingStates: selectedJobLoadingStates
  } = useJobDetails(runId, selectedTestJobId ?? "");
  const jobDetailsLoaded = !!selectedTestJobId && !selectedJobLoadingStates?.details;
  const {
    logs: selectedJobLogs,
    isLoading: isSelectedJobLogsLoading
  } = useStreamingJobLogs(selectedTestJobId ?? "", jobDetailsLoaded, selectedJobDetails?.job?.runner_name, runId);
  const prefetchJobDetails = usePrefetchJobDetails();
  let t28;
  let t29;
  if ($[59] !== currentAttemptJobs || $[60] !== prefetchJobDetails || $[61] !== runId) {
    t28 = () => {
      if (!currentAttemptJobs || !currentAttemptJobs.length) {
        return;
      }
      const blacksmithJobs = currentAttemptJobs.filter(_temp12);
      const failedStatuses = ["failure", "cancelled", "timed_out"];
      const failedBlacksmithJobs = blacksmithJobs.filter(job_0 => failedStatuses.includes(job_0.conclusion?.toLowerCase() || ""));
      const jobsToPrefetch = blacksmithJobs.length < 4 ? blacksmithJobs : failedBlacksmithJobs.slice(0, MAX_FAILED_JOBS_TO_PREFETCH);
      for (const job_1 of jobsToPrefetch) {
        prefetchJobDetails(runId, job_1.id.toString(), {
          includeMetrics: true
        });
      }
    };
    t29 = [currentAttemptJobs, runId, prefetchJobDetails];
    $[59] = currentAttemptJobs;
    $[60] = prefetchJobDetails;
    $[61] = runId;
    $[62] = t28;
    $[63] = t29;
  } else {
    t28 = $[62];
    t29 = $[63];
  }
  useEffect(t28, t29);
  const runTabRowInfoLayoutEnabled = !isLoading && !error && !!data && !!currentAttempt && !!currentAttemptJobs;
  const {
    rowRef: runTabsRowRef,
    tabsViewportRef: runTabsViewportRef,
    pillTabsListRef: runPillTabsListRef,
    infoStripRef: runInfoStripRef,
    showInfoStrip: showRunInfoStrip
  } = useInfoStripCollidesWithTabs(runTabRowInfoLayoutEnabled);
  let t30;
  if ($[64] !== isSlideout || $[65] !== pathname || $[66] !== router) {
    t30 = updates => {
      if (isSlideout) {
        return;
      }
      const currentSearchParams = new URLSearchParams(window.location.search);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) {
          currentSearchParams.delete(key);
        } else {
          currentSearchParams.set(key, value);
        }
      }
      const newUrl = `${pathname}?${currentSearchParams.toString()}`;
      router.replace(newUrl, {
        scroll: false
      });
    };
    $[64] = isSlideout;
    $[65] = pathname;
    $[66] = router;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  const updateUrlParams = t30;
  let t31;
  if ($[68] !== updateUrlParams) {
    t31 = newTab => {
      setActiveTab(newTab);
      updateUrlParams({
        tab: newTab
      });
    };
    $[68] = updateUrlParams;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  const handleTabChange = t31;
  let t32;
  let t33;
  if ($[70] !== selectedAttempt || $[71] !== updateUrlParams) {
    t32 = () => {
      if (selectedAttempt) {
        updateUrlParams({
          attempt: selectedAttempt.toString()
        });
      }
    };
    t33 = [selectedAttempt, updateUrlParams];
    $[70] = selectedAttempt;
    $[71] = updateUrlParams;
    $[72] = t32;
    $[73] = t33;
  } else {
    t32 = $[72];
    t33 = $[73];
  }
  useEffect(t32, t33);
  if (isLoading) {
    let t34;
    if ($[74] !== searchParams) {
      t34 = getActiveRunTab(searchParams);
      $[74] = searchParams;
      $[75] = t34;
    } else {
      t34 = $[75];
    }
    const skeletonVariant = t34 as "overview" | "tests";
    let t35;
    if ($[76] !== data?.workflow_name || $[77] !== updateBrowserTitle) {
      t35 = updateBrowserTitle && <title>{`${data?.workflow_name || "Run Details"} | Blacksmith`}</title>;
      $[76] = data?.workflow_name;
      $[77] = updateBrowserTitle;
      $[78] = t35;
    } else {
      t35 = $[78];
    }
    let t36;
    if ($[79] !== skeletonVariant) {
      t36 = <RunDetailsSkeleton variant={skeletonVariant} />;
      $[79] = skeletonVariant;
      $[80] = t36;
    } else {
      t36 = $[80];
    }
    let t37;
    if ($[81] !== t35 || $[82] !== t36) {
      t37 = <>{t35}{t36}</>;
      $[81] = t35;
      $[82] = t36;
      $[83] = t37;
    } else {
      t37 = $[83];
    }
    return t37;
  }
  if (error) {
    const err = error as Error | AxiosError;
    const errorMessage = err.message || "";
    const is404 = err instanceof AxiosError && err.response?.status === 404 || errorMessage.includes("404") || errorMessage.includes("status code 404");
    let title = "Failed to load workflow run";
    let message = errorMessage || "Unknown error occurred";
    if (is404) {
      title = "Workflow run not found";
      message = "This workflow run may not exist or may still be in progress.";
    }
    let t34;
    if ($[84] !== data?.workflow_name || $[85] !== updateBrowserTitle) {
      t34 = updateBrowserTitle && <title>{`${data?.workflow_name || "Run Details"} | Blacksmith`}</title>;
      $[84] = data?.workflow_name;
      $[85] = updateBrowserTitle;
      $[86] = t34;
    } else {
      t34 = $[86];
    }
    let t35;
    if ($[87] !== message || $[88] !== title) {
      t35 = <ErrorStateDisplay title={title} message={message} />;
      $[87] = message;
      $[88] = title;
      $[89] = t35;
    } else {
      t35 = $[89];
    }
    let t36;
    if ($[90] !== t34 || $[91] !== t35) {
      t36 = <>{t34}{t35}</>;
      $[90] = t34;
      $[91] = t35;
      $[92] = t36;
    } else {
      t36 = $[92];
    }
    return t36;
  }
  if (!data) {
    let t34;
    if ($[93] !== updateBrowserTitle) {
      t34 = updateBrowserTitle && <title>Run Details | Blacksmith</title>;
      $[93] = updateBrowserTitle;
      $[94] = t34;
    } else {
      t34 = $[94];
    }
    let t35;
    if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <ErrorStateDisplay title="Workflow run not found" message="This workflow run may not exist or may still be in progress." />;
      $[95] = t35;
    } else {
      t35 = $[95];
    }
    let t36;
    if ($[96] !== t34) {
      t36 = <>{t34}{t35}</>;
      $[96] = t34;
      $[97] = t36;
    } else {
      t36 = $[97];
    }
    return t36;
  }
  const workflowName = data.workflow_name || "";
  const t34 = `/${org}/runs`;
  let t35;
  if ($[98] !== t34) {
    t35 = <BackLink href={t34} className="type-page-title-l text-neutral-900 dark:text-white">All runs</BackLink>;
    $[98] = t34;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  let t36;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <SlashIcon className="size-4 opacity-50" />;
    $[100] = t36;
  } else {
    t36 = $[100];
  }
  const t37 = currentAttempt?.status || "unknown";
  let t38;
  if ($[101] !== t37) {
    t38 = <StatusIcon status={t37} size={6} />;
    $[101] = t37;
    $[102] = t38;
  } else {
    t38 = $[102];
  }
  let t39;
  if ($[103] !== isSlideout || $[104] !== org || $[105] !== runId || $[106] !== selectedAttempt || $[107] !== workflowName) {
    t39 = isSlideout ? <Link href={`/${org}/runs/${runId}${selectedAttempt ? `?attempt=${selectedAttempt}` : ""}`} className="truncate hover:underline" title="Open full run page">{workflowName}</Link> : <div className="truncate">{workflowName}</div>;
    $[103] = isSlideout;
    $[104] = org;
    $[105] = runId;
    $[106] = selectedAttempt;
    $[107] = workflowName;
    $[108] = t39;
  } else {
    t39 = $[108];
  }
  let t40;
  if ($[109] !== currentAttempt || $[110] !== data || $[111] !== selectedAttempt) {
    t40 = data && currentAttempt && <Link href={data.attempts.length > 1 && !currentAttempt.html_url.includes(`/attempts/${selectedAttempt}`) ? `${currentAttempt.html_url}/attempts/${selectedAttempt}` : currentAttempt.html_url} target="_blank" rel="noopener noreferrer" className="ml-2 p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-transparent focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0" title="View on GitHub"><SiGithub className="w-4 h-4" /></Link>;
    $[109] = currentAttempt;
    $[110] = data;
    $[111] = selectedAttempt;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  let t41;
  if ($[113] !== availableAttempts || $[114] !== selectedAttempt || $[115] !== setSelectedAttempt) {
    t41 = availableAttempts.length > 1 && <Select value={selectedAttempt?.toString() || ""} onValueChange={value_0 => setSelectedAttempt(Number.parseInt(value_0))}><SelectTrigger className="h-7 w-32 ml-2 border-[var(--color-border)] dark:border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-neutral-50 dark:hover:bg-neutral-800/80 transition-colors text-sm px-2"><div className="flex items-center justify-center w-full"><Redo className="w-3.5 h-3.5 mr-1.5 text-neutral-500" /><SelectValue placeholder="Select attempt" className="text-sm" /></div></SelectTrigger><SelectContent>{availableAttempts.map(_temp13)}</SelectContent></Select>;
    $[113] = availableAttempts;
    $[114] = selectedAttempt;
    $[115] = setSelectedAttempt;
    $[116] = t41;
  } else {
    t41 = $[116];
  }
  let t42;
  if ($[117] !== t38 || $[118] !== t39 || $[119] !== t40 || $[120] !== t41) {
    t42 = <h1 className="type-page-title-l text-neutral-900 dark:text-white"><div className="flex items-center gap-2">{t38}{t39}{t40}{t41}</div></h1>;
    $[117] = t38;
    $[118] = t39;
    $[119] = t40;
    $[120] = t41;
    $[121] = t42;
  } else {
    t42 = $[121];
  }
  let t43;
  if ($[122] !== t35 || $[123] !== t42) {
    t43 = <div className="flex gap-4 items-center type-page-title-l">{t35}{t36}{t42}</div>;
    $[122] = t35;
    $[123] = t42;
    $[124] = t43;
  } else {
    t43 = $[124];
  }
  const leftComp = t43;
  let t44;
  bb2: {
    if (!data || !currentAttempt || !currentAttemptJobs) {
      t44 = null;
      break bb2;
    }
    let t45;
    if ($[125] !== currentAttemptJobs || $[126] !== selectedAttempt) {
      t45 = () => {
        const counts = {};
        const rerunJobs = new Set();
        for (const job_2 of currentAttemptJobs) {
          if (job_2.workflow_run_attempt > 1 && job_2.workflow_run_attempt === selectedAttempt) {
            rerunJobs.add(job_2.name);
          }
          const status = (job_2.conclusion || "unknown").toLowerCase();
          counts[status] = (counts[status] || 0) + 1;
        }
        return {
          counts,
          rerunJobs
        };
      };
      $[125] = currentAttemptJobs;
      $[126] = selectedAttempt;
      $[127] = t45;
    } else {
      t45 = $[127];
    }
    const getStatusCounts = t45;
    let t46;
    let t47;
    let t48;
    let t49;
    if ($[128] !== currentAttempt.created_at || $[129] !== currentAttempt.event || $[130] !== currentAttemptJobs.length || $[131] !== data.repository_name || $[132] !== getStatusCounts) {
      const {
        counts: statusCounts,
        rerunJobs: rerunJobs_0
      } = getStatusCounts();
      if ($[137] !== currentAttempt.created_at) {
        t46 = createTimeAgoItem(currentAttempt.created_at);
        $[137] = currentAttempt.created_at;
        $[138] = t46;
      } else {
        t46 = $[138];
      }
      let t50;
      if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
        t50 = <FolderGit2 className="size-3" />;
        $[139] = t50;
      } else {
        t50 = $[139];
      }
      const t51 = data.repository_name || "";
      if ($[140] !== t51) {
        t47 = {
          title: "Repository",
          icon: t50,
          value: t51
        };
        $[140] = t51;
        $[141] = t47;
      } else {
        t47 = $[141];
      }
      if ($[142] !== currentAttempt.event) {
        t48 = createTriggerItem(currentAttempt.event);
        $[142] = currentAttempt.event;
        $[143] = t48;
      } else {
        t48 = $[143];
      }
      t49 = createJobsSummaryItem(currentAttemptJobs.length, statusCounts, rerunJobs_0.size);
      $[128] = currentAttempt.created_at;
      $[129] = currentAttempt.event;
      $[130] = currentAttemptJobs.length;
      $[131] = data.repository_name;
      $[132] = getStatusCounts;
      $[133] = t46;
      $[134] = t47;
      $[135] = t48;
      $[136] = t49;
    } else {
      t46 = $[133];
      t47 = $[134];
      t48 = $[135];
      t49 = $[136];
    }
    let t50;
    if ($[144] !== currentAttempt.created_at || $[145] !== currentAttempt.updated_at) {
      t50 = createDurationItem(currentAttempt.created_at, currentAttempt.updated_at);
      $[144] = currentAttempt.created_at;
      $[145] = currentAttempt.updated_at;
      $[146] = t50;
    } else {
      t50 = $[146];
    }
    let t51;
    if ($[147] !== t46 || $[148] !== t47 || $[149] !== t48 || $[150] !== t49 || $[151] !== t50) {
      t51 = [t46, t47, t48, t49, t50];
      $[147] = t46;
      $[148] = t47;
      $[149] = t48;
      $[150] = t49;
      $[151] = t50;
      $[152] = t51;
    } else {
      t51 = $[152];
    }
    const items = t51;
    t44 = items;
  }
  const infoItems = t44;
  let t45;
  if ($[153] !== data.workflow_name || $[154] !== updateBrowserTitle) {
    t45 = updateBrowserTitle && <title>{`${data.workflow_name || "Run Details"} | Blacksmith`}</title>;
    $[153] = data.workflow_name;
    $[154] = updateBrowserTitle;
    $[155] = t45;
  } else {
    t45 = $[155];
  }
  let t46;
  if ($[156] !== leftComp) {
    t46 = <PageHeader leftComp={leftComp} noPattern={true} />;
    $[156] = leftComp;
    $[157] = t46;
  } else {
    t46 = $[157];
  }
  let t47;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <PillTabsTrigger value="overview"><ListTree className="size-4" />Overview</PillTabsTrigger>;
    $[158] = t47;
  } else {
    t47 = $[158];
  }
  let t48;
  if ($[159] !== failingTestsCount || $[160] !== hasTests || $[161] !== isLoadingTests || $[162] !== isSlideout || $[163] !== org || $[164] !== runId || $[165] !== selectedAttempt) {
    t48 = isSlideout ? <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><div>{hasTests || isLoadingTests ? <Link href={`/${org}/runs/${runId}?tab=tests${selectedAttempt ? `&attempt=${selectedAttempt}` : ""}`} className="cursor-pointer inline-flex items-center justify-center h-8 px-2 py-1 gap-1 rounded-lg text-[13px] tracking-[-0.1px] whitespace-nowrap flex-shrink-0 bg-transparent hover:bg-neutral-50 dark:hover:bg-neutral-800/50 text-neutral-500 dark:text-neutral-400 font-normal hover:text-neutral-900 dark:hover:text-neutral-200 border border-transparent focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0"><FileCheck className="size-4" /><span className="flex items-center gap-1">Tests{isLoadingTests && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 animate-pulse" />}{failingTestsCount > 0 && <span className="bg-red-500/80 text-white text-xs rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center font-medium">{failingTestsCount}</span>}</span><ExternalLink className="size-3 ml-0.5 opacity-60" /></Link> : <span className="inline-flex items-center justify-center h-8 px-2 py-1 gap-1 rounded-lg text-[13px] tracking-[-0.1px] whitespace-nowrap flex-shrink-0 bg-transparent text-neutral-500 dark:text-neutral-400 font-normal border-0 opacity-50 cursor-not-allowed"><FileCheck className="size-4" /><span>Tests</span><ExternalLink className="size-3 ml-0.5 opacity-60" /></span>}</div></TooltipTrigger>{!hasTests && !isLoadingTests && <TooltipContent><p>No tests identified in this workflow run</p></TooltipContent>}</Tooltip></TooltipProvider> : <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><div><PillTabsTrigger value="tests" disabled={!hasTests && !isLoadingTests}><FileCheck className="size-4" /><span className="flex items-center gap-1">Tests{isLoadingTests && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 animate-pulse" />}{failingTestsCount > 0 && <span className="bg-red-500/80 text-white text-xs rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center font-medium">{failingTestsCount}</span>}</span></PillTabsTrigger></div></TooltipTrigger>{!hasTests && !isLoadingTests && <TooltipContent><p>No tests identified in this workflow run</p></TooltipContent>}</Tooltip></TooltipProvider>;
    $[159] = failingTestsCount;
    $[160] = hasTests;
    $[161] = isLoadingTests;
    $[162] = isSlideout;
    $[163] = org;
    $[164] = runId;
    $[165] = selectedAttempt;
    $[166] = t48;
  } else {
    t48 = $[166];
  }
  let t49;
  if ($[167] !== runPillTabsListRef || $[168] !== t48) {
    t49 = <PillTabsList ref={runPillTabsListRef}>{t47}{t48}</PillTabsList>;
    $[167] = runPillTabsListRef;
    $[168] = t48;
    $[169] = t49;
  } else {
    t49 = $[169];
  }
  let t50;
  if ($[170] !== runTabsViewportRef || $[171] !== t49) {
    t50 = <div ref={runTabsViewportRef} className="min-w-0 flex-1 overflow-x-auto">{t49}</div>;
    $[170] = runTabsViewportRef;
    $[171] = t49;
    $[172] = t50;
  } else {
    t50 = $[172];
  }
  let t51;
  if ($[173] !== infoItems || $[174] !== runInfoStripRef || $[175] !== showRunInfoStrip) {
    t51 = infoItems && <div ref={runInfoStripRef} className="flex-shrink-0">{showRunInfoStrip && <InfoCard items={infoItems} variant="strip" />}</div>;
    $[173] = infoItems;
    $[174] = runInfoStripRef;
    $[175] = showRunInfoStrip;
    $[176] = t51;
  } else {
    t51 = $[176];
  }
  let t52;
  if ($[177] !== runTabsRowRef || $[178] !== t50 || $[179] !== t51) {
    t52 = <div className="px-6 pt-app-header-gap pb-0 bg-app-background"><div ref={runTabsRowRef} className="flex items-center gap-4">{t50}{t51}</div></div>;
    $[177] = runTabsRowRef;
    $[178] = t50;
    $[179] = t51;
    $[180] = t52;
  } else {
    t52 = $[180];
  }
  const t53 = activeTab === "overview" ? "opacity-100 pointer-events-auto z-10" : "opacity-0 pointer-events-none z-0";
  let t54;
  if ($[181] !== t53) {
    t54 = cn("absolute inset-0 flex flex-col transition-opacity duration-0 overflow-y-auto p-6 bg-app-background", t53);
    $[181] = t53;
    $[182] = t54;
  } else {
    t54 = $[182];
  }
  const t55 = activeTab !== "overview" ? true : undefined;
  const t56 = activeTab !== "overview";
  let t57;
  if ($[183] !== currentAttemptJobs) {
    t57 = currentAttemptJobs || [];
    $[183] = currentAttemptJobs;
    $[184] = t57;
  } else {
    t57 = $[184];
  }
  const t58 = activeTab === "overview";
  let t59;
  if ($[185] !== org || $[186] !== runId || $[187] !== selectedAttempt || $[188] !== t57 || $[189] !== t58) {
    t59 = <RunDetailsJobsList org={org} runId={runId} jobs={t57} selectedAttempt={selectedAttempt} isActive={t58} />;
    $[185] = org;
    $[186] = runId;
    $[187] = selectedAttempt;
    $[188] = t57;
    $[189] = t58;
    $[190] = t59;
  } else {
    t59 = $[190];
  }
  let t60;
  if ($[191] !== t54 || $[192] !== t55 || $[193] !== t56 || $[194] !== t59) {
    t60 = <div className={t54} inert={t55} aria-hidden={t56}>{t59}</div>;
    $[191] = t54;
    $[192] = t55;
    $[193] = t56;
    $[194] = t59;
    $[195] = t60;
  } else {
    t60 = $[195];
  }
  const t61 = activeTab === "tests" ? "opacity-100 pointer-events-auto z-10" : "opacity-0 pointer-events-none z-0";
  let t62;
  if ($[196] !== t61) {
    t62 = cn("absolute inset-0 transition-opacity duration-0", t61);
    $[196] = t61;
    $[197] = t62;
  } else {
    t62 = $[197];
  }
  const t63 = activeTab !== "tests" ? true : undefined;
  const t64 = activeTab !== "tests";
  let t65;
  if ($[198] !== activeTab || $[199] !== branchFilter || $[200] !== data.head_branch || $[201] !== data.pull_request?.number || $[202] !== data.repository_name || $[203] !== isLoadingTests || $[204] !== isPendingDefaultFilter || $[205] !== isSelectedJobLogsLoading || $[206] !== jobFilters || $[207] !== org || $[208] !== runId || $[209] !== selectedJobDetails?.job?.steps || $[210] !== selectedJobLoadingStates?.details || $[211] !== selectedJobLogs || $[212] !== selectedTestId || $[213] !== selectedTestJobId || $[214] !== setJobFilters || $[215] !== setSelectedTestId || $[216] !== setStatusFilters || $[217] !== statusFilters || $[218] !== testsAsTestChecks || $[219] !== testsError || $[220] !== timeRangeHours || $[221] !== zoomEnd || $[222] !== zoomStart) {
    t65 = testsError ? <div className="p-6"><div className="border rounded-lg bg-[var(--color-surface)] shadow-sm p-4"><div className="text-red-500 text-sm">Failed to load tests</div></div></div> : isLoadingTests || isPendingDefaultFilter ? <div className="flex flex-col flex-1 min-h-0 overflow-y-auto bg-app-background"><TestListSkeleton /></div> : <TestList checks={testsAsTestChecks} isLoading={isLoadingTests} isActive={activeTab === "tests"} repository={data.repository_name || ""} org={org} runId={runId} jobId={undefined} branch={data.head_branch || null} prNumber={data.pull_request?.number ?? null} selectedTestId={selectedTestId} onTestSelect={test => setSelectedTestId(test?.id ?? null)} statusFilters={statusFilters} timeRangeHours={timeRangeHours} branchFilter={branchFilter} zoomStart={zoomStart} zoomEnd={zoomEnd} onStatusFiltersChange={setStatusFilters} onTimeRangeChange={setTimeRangeHours} onBranchFilterChange={setBranchFilter} onZoomChange={(start, end) => {
      setZoomStart(start);
      setZoomEnd(end);
    }} isPendingDefaultFilter={isPendingDefaultFilter} jobFilters={jobFilters} onJobFiltersChange={setJobFilters} logs={selectedJobLogs} steps={selectedJobDetails?.job?.steps} isLogsLoading={!!selectedTestJobId && selectedJobLoadingStates?.details || isSelectedJobLogsLoading} />;
    $[198] = activeTab;
    $[199] = branchFilter;
    $[200] = data.head_branch;
    $[201] = data.pull_request?.number;
    $[202] = data.repository_name;
    $[203] = isLoadingTests;
    $[204] = isPendingDefaultFilter;
    $[205] = isSelectedJobLogsLoading;
    $[206] = jobFilters;
    $[207] = org;
    $[208] = runId;
    $[209] = selectedJobDetails?.job?.steps;
    $[210] = selectedJobLoadingStates?.details;
    $[211] = selectedJobLogs;
    $[212] = selectedTestId;
    $[213] = selectedTestJobId;
    $[214] = setJobFilters;
    $[215] = setSelectedTestId;
    $[216] = setStatusFilters;
    $[217] = statusFilters;
    $[218] = testsAsTestChecks;
    $[219] = testsError;
    $[220] = timeRangeHours;
    $[221] = zoomEnd;
    $[222] = zoomStart;
    $[223] = t65;
  } else {
    t65 = $[223];
  }
  let t66;
  if ($[224] !== t62 || $[225] !== t63 || $[226] !== t64 || $[227] !== t65) {
    t66 = <div className={t62} inert={t63} aria-hidden={t64}>{t65}</div>;
    $[224] = t62;
    $[225] = t63;
    $[226] = t64;
    $[227] = t65;
    $[228] = t66;
  } else {
    t66 = $[228];
  }
  let t67;
  if ($[229] !== t60 || $[230] !== t66) {
    t67 = <div className="flex-1 min-h-0 relative">{t60}{t66}</div>;
    $[229] = t60;
    $[230] = t66;
    $[231] = t67;
  } else {
    t67 = $[231];
  }
  let t68;
  if ($[232] !== activeTab || $[233] !== handleTabChange || $[234] !== t52 || $[235] !== t67) {
    t68 = <div className="flex flex-col flex-1"><Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col flex-1">{t52}{t67}</Tabs></div>;
    $[232] = activeTab;
    $[233] = handleTabChange;
    $[234] = t52;
    $[235] = t67;
    $[236] = t68;
  } else {
    t68 = $[236];
  }
  let t69;
  if ($[237] !== t45 || $[238] !== t46 || $[239] !== t68) {
    t69 = <div className="flex flex-col flex-1">{t45}{t46}{t68}</div>;
    $[237] = t45;
    $[238] = t46;
    $[239] = t68;
    $[240] = t69;
  } else {
    t69 = $[240];
  }
  return t69;
}
function _temp13(attemptNum) {
  return <SelectItem key={attemptNum} value={attemptNum.toString()} className="text-sm">Attempt {attemptNum}</SelectItem>;
}
function _temp12(job) {
  const labels = job.labels || [];
  const runnerLabel = labels[0] || "unknown";
  return isBlacksmithRunner(runnerLabel);
}
function _temp11(t_0) {
  return t_0.test_status === "fail" || t_0.test_status === "error";
}
function _temp10(t) {
  return t.test_status === "fail" || t.test_status === "error";
}
function _temp9() {
  return new URLSearchParams(window.location.search).get("test_status");
}
function _temp8(params) {
  return params.getAll("jobs").filter(Boolean);
}
function _temp(job_1) {
  const labels = job_1.labels || [];
  const runnerLabel = labels[0] || "unknown";
  return isBlacksmithRunner(runnerLabel);
}
function _temp2(group) {
  const failedStatuses = ["failure", "cancelled", "timed_out"];
  const hasFailedJob = group.jobs.some(job_0 => failedStatuses.includes(job_0.conclusion?.toLowerCase() || ""));
  const hasBlacksmithJob = group.jobs.some(_temp);
  if (hasBlacksmithJob && hasFailedJob) {
    return 0;
  }
  if (hasBlacksmithJob && !hasFailedJob) {
    return 1;
  }
  if (!hasBlacksmithJob && hasFailedJob) {
    return 2;
  }
  return 3;
}
function _temp3(a_0, b_0) {
  const failedStatuses_0 = ["failure", "cancelled", "timed_out"];
  const aIsFailed = failedStatuses_0.includes(a_0.conclusion?.toLowerCase() || "");
  const bIsFailed = failedStatuses_0.includes(b_0.conclusion?.toLowerCase() || "");
  const aLabels = a_0.labels || [];
  const bLabels = b_0.labels || [];
  const aIsBlacksmith = isBlacksmithRunner(aLabels[0] || "unknown");
  const bIsBlacksmith = isBlacksmithRunner(bLabels[0] || "unknown");
  const aPriority = aIsBlacksmith ? aIsFailed ? 0 : 1 : aIsFailed ? 2 : 3;
  const bPriority = bIsBlacksmith ? bIsFailed ? 0 : 1 : bIsFailed ? 2 : 3;
  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }
  return a_0.name.localeCompare(b_0.name);
}
function _temp4(job_3) {
  const labels_1 = job_3.labels || [];
  const runnerLabel_1 = labels_1[0] || "unknown";
  return isBlacksmithRunner(runnerLabel_1);
}
function _temp5(prev) {
  return Math.max(prev - 1, 0);
}
function _temp6(job_4) {
  return job_4.runtime_seconds;
}
function _temp7(a_1, b_1) {
  const failedStatuses_1 = ["failure", "cancelled", "timed_out"];
  const aIsFailed_0 = failedStatuses_1.includes(a_1.conclusion?.toLowerCase() || "");
  const bIsFailed_0 = failedStatuses_1.includes(b_1.conclusion?.toLowerCase() || "");
  const aLabels_0 = a_1.labels || [];
  const bLabels_0 = b_1.labels || [];
  const aIsBlacksmith_0 = isBlacksmithRunner(aLabels_0[0] || "unknown");
  const bIsBlacksmith_0 = isBlacksmithRunner(bLabels_0[0] || "unknown");
  const aPriority_0 = aIsBlacksmith_0 ? aIsFailed_0 ? 0 : 1 : aIsFailed_0 ? 2 : 3;
  const bPriority_0 = bIsBlacksmith_0 ? bIsFailed_0 ? 0 : 1 : bIsFailed_0 ? 2 : 3;
  if (aPriority_0 !== bPriority_0) {
    return aPriority_0 - bPriority_0;
  }
  return a_1.name.localeCompare(b_1.name);
}